import React from "react";
import { formatValue } from "./widgetFormat";

/**
 * Drill-down modal — shows the widget's raw payload in a friendly card and
 * offers a "Ver detalle completo →" button that switches the SPA tab and
 * emits the pre-filters carried by the widget definition.
 *
 * Kept intentionally generic so any widget type can reuse it.
 */
export default function DrilldownModal({ open, definition, payload, onClose, onNavigate }) {
  if (!open || !definition || !payload) return null;
  const data = payload.data || {};
  const drilldown = payload.drilldown || {};

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-6"
      onClick={onClose}
      data-testid="dashboard-drilldown-modal"
    >
      <div
        className="bg-white w-full sm:max-w-2xl max-h-[90vh] overflow-auto border border-black"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[var(--border)] p-4 sm:p-5">
          <div>
            <div className="font-mono-tech text-[0.6rem] font-bold tracking-[0.25em] text-[var(--text-secondary)]">
              DRILL-DOWN · {definition.category?.toUpperCase() || "WIDGET"}
            </div>
            <div className="font-display font-black text-xl sm:text-2xl tracking-tight mt-0.5">
              {definition.title}
            </div>
            {definition.description && (
              <div className="text-xs text-[var(--text-secondary)] mt-1">
                {definition.description}
              </div>
            )}
          </div>
          <button
            data-testid="dashboard-drilldown-close"
            onClick={onClose}
            className="font-mono-tech text-sm border border-[var(--border)] w-9 h-9 hover:bg-black hover:text-white transition-colors"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          <DetailBody definition={definition} data={data} />
        </div>

        <div className="flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-2 border-t border-[var(--border)] p-4 bg-[var(--bg)]">
          <div className="font-mono-tech text-[0.6rem] tracking-wider text-[var(--text-secondary)]">
            Generado: {new Date(payload.generated_at).toLocaleString()}
          </div>
          {drilldown.panel && (
            <button
              data-testid="dashboard-drilldown-navigate"
              onClick={() => onNavigate?.(drilldown)}
              className="font-display font-bold text-sm bg-black text-white px-4 py-2 hover:bg-[#0E5FFF] transition-colors"
            >
              {drilldown.label || "Ver detalle completo →"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function DetailBody({ definition, data }) {
  if (definition.type === "kpi") {
    return (
      <div className="space-y-3">
        <div className="border border-[var(--border)] p-4">
          <div className="font-mono-tech text-[0.65rem] tracking-wider text-[var(--text-secondary)]">
            VALOR ACTUAL
          </div>
          <div className="font-mono-tech font-black text-4xl tabular-nums mt-1">
            {formatValue(data.value, data.format)}
          </div>
          {data.trend_label && (
            <div className="mt-2 text-sm">
              {data.trend_pct !== null && data.trend_pct !== undefined && (
                <span className="font-mono-tech font-bold mr-2">
                  {Number(data.trend_pct) >= 0 ? "▲" : "▼"}{" "}
                  {Math.abs(Number(data.trend_pct)).toFixed(1)}%
                </span>
              )}
              <span className="text-[var(--text-secondary)]">{data.trend_label}</span>
            </div>
          )}
        </div>
        {data.detail && (
          <div className="border border-[var(--border)] p-4">
            <div className="font-mono-tech text-[0.65rem] tracking-wider text-[var(--text-secondary)] mb-2">
              DETALLE
            </div>
            <dl className="grid grid-cols-2 gap-2 text-sm">
              {Object.entries(data.detail).map(([k, v]) => (
                <React.Fragment key={k}>
                  <dt className="text-[var(--text-secondary)] capitalize">
                    {k.replace(/_/g, " ")}
                  </dt>
                  <dd className="font-mono-tech tabular-nums text-right">
                    {typeof v === "number"
                      ? formatValue(v, data.format || "int")
                      : String(v)}
                  </dd>
                </React.Fragment>
              ))}
            </dl>
          </div>
        )}
      </div>
    );
  }

  if (definition.type === "chart" && Array.isArray(data.series)) {
    return (
      <div className="border border-[var(--border)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border)] bg-[var(--bg)]">
              <th className="text-left font-mono-tech text-[0.65rem] tracking-wider px-3 py-2">
                FECHA
              </th>
              <th className="text-right font-mono-tech text-[0.65rem] tracking-wider px-3 py-2">
                VALOR
              </th>
            </tr>
          </thead>
          <tbody>
            {data.series.slice(-14).reverse().map((p, i) => (
              <tr key={i} className="border-b border-[var(--border)] last:border-b-0">
                <td className="px-3 py-1.5 font-mono-tech">{p.date}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">
                  {formatValue(p.value, data.y_format || "int")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (definition.type === "table" && Array.isArray(data.rows)) {
    return (
      <div className="border border-[var(--border)] overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border)] bg-[var(--bg)]">
              {(data.columns || []).map((c) => (
                <th
                  key={c.key}
                  className="text-left font-mono-tech text-[0.65rem] tracking-wider px-3 py-2"
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r, i) => (
              <tr key={i} className="border-b border-[var(--border)] last:border-b-0">
                {(data.columns || []).map((c) => (
                  <td key={c.key} className="px-3 py-1.5 tabular-nums">
                    {c.format
                      ? formatValue(r[c.key], c.format)
                      : String(r[c.key] ?? "—")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (definition.type === "pie" && Array.isArray(data.slices)) {
    return (
      <div className="border border-[var(--border)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--border)] bg-[var(--bg)]">
              <th className="text-left font-mono-tech text-[0.65rem] tracking-wider px-3 py-2">
                CATEGORÍA
              </th>
              <th className="text-right font-mono-tech text-[0.65rem] tracking-wider px-3 py-2">
                CANTIDAD
              </th>
              <th className="text-right font-mono-tech text-[0.65rem] tracking-wider px-3 py-2">
                %
              </th>
            </tr>
          </thead>
          <tbody>
            {data.slices.map((s, i) => {
              const total = data.total || 1;
              return (
                <tr key={i} className="border-b border-[var(--border)] last:border-b-0">
                  <td className="px-3 py-1.5">{s.label}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{s.value}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {((s.value / total) * 100).toFixed(0)}%
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <pre className="text-xs bg-[var(--bg)] p-3 overflow-auto max-h-72">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}
